import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { confirmDialog } from '../utils/confirm';
import { usePagination } from '../components/Pagination';
import { exportStaffCSV } from '../utils/export.helpers';

const TYPES = { ENSEIGNANT: 'Enseignant', SOUTIEN: 'Soutien', ADMINISTRATIF: 'Administratif', FONDATEUR: 'Fondateur' };
const STATUS = { ACTIF: ['green', 'Actif'], INACTIF: ['gray', 'Inactif'], SUSPENDU: ['red', 'Suspendu'] };

const EMPTY = {
  matricule: '', civility: 'M.', firstName: '', lastName: '', gender: 'M', type: 'ENSEIGNANT',
  birthDate: '', birthPlace: '', qualification: '', diploma: '', cnib: '', cnss: '',
  hireDate: '', status: 'ACTIF', function: '', phone: '', whatsapp: '', email: '',
  subject1: '', subject2: '', weeklyHours: '',
};

export default function Staff() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [type, setType] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [view, setView] = useState(null);
  const [subjects, setSubjects] = useState([]);
  useEffect(() => { api.get(`/catalog/subjects`).then(setSubjects).catch(() => {}); }, []);

  function load() {
    const qs = [];
    if (type) qs.push(`type=${type}`);
    if (search.trim()) qs.push(`search=${encodeURIComponent(search.trim())}`);
    api.get(`/staff${qs.length ? `?${qs.join('&')}` : ''}`).then(setRows).catch((e) => setError(e.message));
    api.get('/staff/stats').then(setStats).catch(() => {});
  }
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [type, search]);

  async function archive(id) {
    if (!(await confirmDialog({ title: 'Archiver', message: 'Archiver cet agent ?', confirmLabel: 'Archiver', danger: true }))) return;
    try { await api.del(`/staff/${id}`); load(); } catch (e) { setError(e.message); }
  }

  // Pagination sur les rows déjà filtrés par le backend
  const { paginated, Pagination, total } = usePagination(rows, { pageSize: 5, searchKeys: [] });

  return (
    <>
      {stats && (
        <div className="kpi-grid">
          <div className="kpi navy"><span>Personnel total</span><strong>{stats.total}</strong></div>
          <div className="kpi"><span>Enseignants</span><strong>{stats.ENSEIGNANT}</strong></div>
          <div className="kpi"><span>Soutien</span><strong>{stats.SOUTIEN}</strong></div>
          <div className="kpi gold"><span>Administratif</span><strong>{stats.ADMINISTRATIF}</strong></div>
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h2>Registre du personnel</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select style={{ width: 150 }} value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">Tous les types</option>
              {Object.entries(TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', fontSize: 13 }}>🔍</span>
              <input style={{ paddingLeft: 28, width: 200 }} placeholder="Nom ou matricule…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <button className="btn-ghost btn-sm" onClick={() => exportStaffCSV(rows)}>↓ Excel</button>
            <button className="btn btn-sm" onClick={() => setModal({ ...EMPTY })}>+ Ajouter</button>
          </div>
        </div>
        <div className="panel-body">
          {error && <div className="form-error">{error}</div>}
          {rows.length === 0 ? <div className="empty">Aucun agent.</div> : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Agent</th><th>Type</th><th>Fonction / Qualif.</th><th>Contact</th><th>Statut</th><th></th></tr>
                </thead>
                <tbody>
                  {paginated.map((s) => {
                    const [b, l] = STATUS[s.status] || ['gray', s.status];
                    return (
                      <tr key={s.id}>
                        <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="avatar-initials" style={{ background: s.type === 'ENSEIGNANT' ? '#2563eb' : s.type === 'ADMINISTRATIF' ? '#7c3aed' : '#16a34a' }}>
                            {(s.firstName?.[0] || '')}{(s.lastName?.[0] || '')}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 12 }}>{s.civility} {s.lastName} {s.firstName}</div>
                            <div className="mono" style={{ fontSize: 10, color: 'var(--muted-light)' }}>{s.matricule}</div>
                          </div>
                        </div>
                      </td>
                        <td>{TYPES[s.type] || s.type}</td>
                        <td>{s.function || s.qualification || '—'}{s.type === 'ENSEIGNANT' && s.subject1 ? ` · ${s.subject1}` : ''}</td>
                        <td>{s.phone || s.whatsapp || ''}</td>
                        <td><span className={`badge ${b}`}>{l}</span></td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn-ghost btn-sm" onClick={() => setView(s)}>Fiche</button>
                            <button className="btn-ghost btn-sm" onClick={() => setModal(s)}>Modifier</button>
                            <button className="link-danger" onClick={() => archive(s.id)}>Archiver</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pagination />
            </div>
          )}
        </div>
      </div>

      {modal && <StaffModal initial={modal} subjects={subjects} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {view && <StaffView s={view} onClose={() => setView(null)} onEdit={() => { setModal(view); setView(null); }} />}
    </>
  );
}

function StaffModal({ initial, onClose, onSaved, subjects }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({
    ...EMPTY, ...initial,
    birthDate: initial.birthDate ? String(initial.birthDate).slice(0, 10) : '',
    hireDate: initial.hireDate ? String(initial.hireDate).slice(0, 10) : '',
    weeklyHours: initial.weeklyHours ?? '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault(); setError(''); setSaving(true);
    const payload = {
      matricule: f.matricule, firstName: f.firstName, lastName: f.lastName, type: f.type,
      civility: f.civility || undefined, gender: f.gender || undefined,
      birthDate: f.birthDate || undefined, birthPlace: f.birthPlace || undefined,
      qualification: f.qualification || undefined, diploma: f.diploma || undefined,
      cnib: f.cnib || undefined, cnss: f.cnss || undefined,
      hireDate: f.hireDate || undefined, status: f.status || undefined,
      function: f.function || undefined, phone: f.phone || undefined,
      whatsapp: f.whatsapp || undefined, email: f.email || undefined,
      subject1: f.subject1 || undefined, subject2: f.subject2 || undefined,
      weeklyHours: f.weeklyHours !== '' ? Number(f.weeklyHours) : undefined,
    };
    try {
      if (isEdit) await api.patch(`/staff/${initial.id}`, payload);
      else await api.post('/staff', payload);
      onSaved();
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginBottom: 16 }}>{isEdit ? "Modifier l'agent" : 'Nouvel agent'}</h2>
        {error && <div className="form-error">{error}</div>}
        <form onSubmit={submit}>
          <div className="fieldset-title">Identité</div>
          <div className="grid-3">
            <div className="field"><label>Matricule *</label><input value={f.matricule} onChange={set('matricule')} required /></div>
            <div className="field"><label>Civilité</label>
              <select value={f.civility} onChange={e => setF({ ...f, civility: e.target.value, gender: e.target.value === 'M.' ? 'M' : 'F' })}><option>M.</option><option>Mme</option><option>Mlle</option></select></div>
            <div className="field"><label>Sexe</label>
              <select value={f.gender} onChange={e => setF({ ...f, gender: e.target.value, civility: e.target.value === 'M' ? 'M.' : (f.civility === 'M.' ? 'Mme' : f.civility) })}><option value="M">Masculin</option><option value="F">Féminin</option></select></div>
            <div className="field"><label>Nom *</label><input value={f.lastName} onChange={set('lastName')} required /></div>
            <div className="field"><label>Prénom *</label><input value={f.firstName} onChange={set('firstName')} required /></div>
            <div className="field"><label>Date de naissance</label><input type="date" value={f.birthDate} onChange={set('birthDate')} /></div>
            <div className="field"><label>Lieu de naissance</label><input value={f.birthPlace} onChange={set('birthPlace')} /></div>
          </div>

          <div className="fieldset-title">Poste</div>
          <div className="grid-3">
            <div className="field"><label>Type *</label>
              <select value={f.type} onChange={set('type')}>{Object.entries(TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div className="field"><label>Statut</label>
              <select value={f.status} onChange={set('status')}><option value="ACTIF">Actif</option><option value="INACTIF">Inactif</option><option value="SUSPENDU">Suspendu</option></select></div>
            <div className="field"><label>Prise de service</label><input type="date" value={f.hireDate} onChange={set('hireDate')} /></div>
            <div className="field"><label>Fonction</label><input value={f.function} onChange={set('function')} /></div>
            <div className="field"><label>Qualification</label><input value={f.qualification} onChange={set('qualification')} /></div>
            <div className="field"><label>Diplôme</label><input value={f.diploma} onChange={set('diploma')} /></div>
          </div>

          {f.type === 'ENSEIGNANT' && (
            <>
              <div className="fieldset-title">Enseignement</div>
              <div className="grid-3">
                <div className="field"><label>Matière 1</label><select value={f.subject1} onChange={set('subject1')}><option value="">—</option>{subjects.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}</select></div>
                <div className="field"><label>Matière 2</label><select value={f.subject2} onChange={set('subject2')}><option value="">—</option>{subjects.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}</select></div>
                <div className="field"><label>Volume horaire</label><input type="number" min="0" value={f.weeklyHours} onChange={set('weeklyHours')} /></div>
              </div>
            </>
          )}

          <div className="fieldset-title">Administratif & contact</div>
          <div className="grid-3">
            <div className="field"><label>Réf. CNIB</label><input value={f.cnib} onChange={set('cnib')} /></div>
            <div className="field"><label>N° CNSS</label><input value={f.cnss} onChange={set('cnss')} /></div>
            <div className="field"><label>Téléphone</label><input value={f.phone} onChange={set('phone')} /></div>
            <div className="field"><label>WhatsApp</label><input value={f.whatsapp} onChange={set('whatsapp')} /></div>
            <div className="field"><label>E-mail</label><input value={f.email} onChange={set('email')} /></div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button className="btn" type="submit" disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>
              {saving ? 'Enregistrement…' : isEdit ? 'Enregistrer' : "Créer l'agent"}
            </button>
            <button type="button" className="btn-ghost" onClick={onClose}>Annuler</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StaffView({ s, onClose, onEdit }) {
  const R = (label, value) => (
    <div className="view-row"><span className="view-label">{label}</span><span className="view-value">{value != null && value !== '' ? value : '—'}</span></div>
  );
  const d = (v) => (v ? new Date(v).toLocaleDateString('fr-FR') : '—');
  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginBottom: 4 }}>{s.civility} {s.lastName} {s.firstName}</h2>
        <div className="mono" style={{ color: 'var(--muted)', marginBottom: 12 }}>{s.matricule} · {TYPES[s.type] || s.type}</div>
        <div className="fieldset-title">Identité</div>
        <div className="view-grid">
          {R('Sexe', s.gender === 'F' ? 'Féminin' : 'Masculin')}
          {R('Date de naissance', d(s.birthDate))}{R('Lieu de naissance', s.birthPlace)}
        </div>
        <div className="fieldset-title">Poste</div>
        <div className="view-grid">
          {R('Type', TYPES[s.type] || s.type)}{R('Statut', (STATUS[s.status] || [])[1] || s.status)}
          {R('Prise de service', d(s.hireDate))}{R('Fonction', s.function)}
          {R('Qualification', s.qualification)}{R('Diplôme', s.diploma)}
        </div>
        {s.type === 'ENSEIGNANT' && (
          <>
            <div className="fieldset-title">Enseignement</div>
            <div className="view-grid">{R('Matière 1', s.subject1)}{R('Matière 2', s.subject2)}{R('Volume horaire', s.weeklyHours)}</div>
          </>
        )}
        <div className="fieldset-title">Contact</div>
        <div className="view-grid">
          {R('Réf. CNIB', s.cnib)}{R('N° CNSS', s.cnss)}
          {R('Téléphone', s.phone)}{R('WhatsApp', s.whatsapp)}{R('E-mail', s.email)}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button className="btn" onClick={onEdit} style={{ width: 'auto', padding: '0 22px' }}>Modifier</button>
          <button type="button" className="btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
